import {
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT, SLIDE_FEATHER_ICON} from '#/lib/slideTokens'
import {type SlideEditorProps} from '#/components/capsules/play/types'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {readableAccent, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {SLIDE_TYPE_META} from '#/types/capsule'

const GRID_SIZE = 8
const MAX_WORDS = 10
const ALPHA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
// H, V, diagonal right-down only — matches source placement algorithm
const DIRS: [number, number][] = [
  [0, 1],
  [1, 0],
  [1, 1],
]

function buildGrid(targets: string[], size = GRID_SIZE): string[][] {
  // Start empty, place words with conflict detection, then fill remainder
  const grid: string[][] = Array.from({length: size}, () =>
    Array(size).fill(''),
  )

  for (const word of targets) {
    const w = word.toUpperCase().replace(/[^A-Z]/g, '')
    let ok = false
    for (let attempt = 0; attempt < 200 && !ok; attempt++) {
      const [dr, dc] = DIRS[Math.floor(Math.random() * DIRS.length)]
      const maxR = size - dr * w.length
      const maxC = size - dc * w.length
      if (maxR <= 0 || maxC <= 0) continue
      const r = Math.floor(Math.random() * maxR)
      const c = Math.floor(Math.random() * maxC)
      let conflict = false
      for (let i = 0; i < w.length && !conflict; i++) {
        const cell = grid[r + i * dr]?.[c + i * dc]
        if (cell && cell !== w[i]) conflict = true
      }
      if (!conflict) {
        for (let i = 0; i < w.length; i++) grid[r + i * dr][c + i * dc] = w[i]
        ok = true
      }
    }
  }

  // Fill remaining empty cells with random letters
  for (let r = 0; r < size; r++)
    for (let c = 0; c < size; c++)
      if (!grid[r][c]) grid[r][c] = ALPHA[Math.floor(Math.random() * 26)]

  return grid
}

export function FindWordsSlideEditor({
  slide,
  onChange,
  primaryColor: _primaryColor,
  onPreview,
}: SlideEditorProps) {
  const ss = useStyles()
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const accent = SLIDE_ACCENT.find_words
  // Ink, not fill: the taxonomy's colour tuned to read on the studio surface it
  // is written on (see `readableAccent`). Tints stay on the raw hue.
  const accentInk = readableAccent(accent, chrome)
  const meta = SLIDE_TYPE_META.find_words
  const slideIcon = SLIDE_FEATHER_ICON.find_words ?? 'help-circle'
  const targets: string[] = slide.findWordsTargets ?? []
  const grid: string[][] = slide.findWordsGrid ?? []

  const addWord = () => {
    if (targets.length >= MAX_WORDS) return
    onChange({findWordsTargets: [...targets, ''], findWordsGrid: []})
  }

  const removeWord = (i: number) => {
    const next = targets.filter((_, j) => j !== i)
    onChange({findWordsTargets: next, findWordsGrid: []})
  }

  const updateWord = (i: number, val: string) => {
    const next = [...targets]
    next[i] = val.toUpperCase()
    onChange({findWordsTargets: next, findWordsGrid: []})
  }

  const generateGrid = () => {
    onChange({findWordsGrid: buildGrid(targets)})
  }

  return (
    <ScrollView
      style={ss.root}
      contentContainerStyle={ss.inner}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}>
      {/* Type badge row */}
      {/* Host-provided header (e.g. FlashBook studio) replaces this row */}
      <View style={[ss.topRow, onPreview ? null : {display: 'none'}]}>
        <View
          style={[
            ss.typeBadge,
            {
              backgroundColor: accent + '22',
              borderColor: accent + '44',
            },
          ]}>
          <Feather name={slideIcon} size={12} color={accentInk} />
          <Text style={[ss.typeBadgeText, {color: accentInk}]}>
            {meta?.label ?? slide.type}
          </Text>
        </View>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={onPreview}
          hitSlop={6}
          style={[
            ss.previewBtn,
            {
              backgroundColor: accent + '18',
              borderColor: accent + '55',
            },
          ]}>
          <Feather name="play-circle" size={13} color={accentInk} />
          <Text
            style={[ss.previewBtnText, {color: accentInk}]}>{l`Preview`}</Text>
        </TouchableOpacity>
      </View>

      {/* Question */}
      <StudioTextField
        accessibilityLabel="Text input field"
        accessibilityHint={undefined}
        style={ss.question}
        value={slide.question ?? ''}
        onChangeText={v => onChange({question: v})}
        placeholder={l`Écris ta question ici…`}
        placeholderTextColor={chrome.textFaint}
      />

      {/* Words */}
      <Text style={ss.sectionLabel}>
        {l`MOTS À TROUVER (en majuscules)`.toUpperCase()}
      </Text>
      {targets.map((word, i) => (
        <View key={i} style={ss.wordRow}>
          <View style={ss.wordCard}>
            <TextInput
              accessibilityLabel="Text input field"
              accessibilityHint={undefined}
              style={ss.wordInput}
              value={word}
              onChangeText={v => updateWord(i, v)}
              placeholder={`MOT ${i + 1}`}
              placeholderTextColor={chrome.textFaint}
              autoCapitalize="characters"
            />
          </View>
          {targets.length > 1 && (
            <TouchableOpacity
              accessibilityRole="button"
              onPress={() => removeWord(i)}>
              <Feather name="trash-2" size={14} color={chrome.textFaint} />
            </TouchableOpacity>
          )}
        </View>
      ))}
      {targets.length < MAX_WORDS && (
        <TouchableOpacity
          accessibilityRole="button"
          onPress={addWord}
          style={ss.addBtn}>
          <Feather name="plus" size={14} color={chrome.textFaint} />
          <Text style={ss.addTxt}>{l`Add a word`}</Text>
        </TouchableOpacity>
      )}

      {/* Generate grid */}
      <TouchableOpacity
        accessibilityRole="button"
        onPress={generateGrid}
        style={[
          ss.generateBtn,
          {
            borderColor: accentInk,
            backgroundColor: accent + '18',
          },
        ]}>
        <Feather name="grid" size={14} color={accentInk} />
        <Text style={[ss.generateTxt, {color: accentInk}]}>
          {grid.length > 0 ? l`Regénérer la grille` : l`Générer la grille`}
        </Text>
      </TouchableOpacity>

      {grid.length > 0 && (
        <View style={ss.gridStatus}>
          <Text style={ss.gridStatusText}>
            {`✓ Grille générée (${grid[0]?.length ?? 0}×${grid.length})`}
          </Text>
        </View>
      )}

      {/* Explanation */}
      <View style={ss.explanationWrap}>
        <Text style={ss.explanationLabel}>
          {l`EXPLICATION (optionnelle)`.toUpperCase()}
        </Text>
        <View style={ss.explanationCard}>
          <StudioTextField
            accessibilityLabel="Text input field"
            accessibilityHint={undefined}
            style={ss.explanationInput}
            value={slide.explanation ?? ''}
            onChangeText={v => onChange({explanation: v})}
            placeholder={l`Explication affichée après la réponse…`}
            placeholderTextColor={chrome.textFaint}
            textAlignVertical="top"
          />
        </View>
      </View>
    </ScrollView>
  )
}

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    root: {flex: 1, backgroundColor: chrome.frame},
    inner: {padding: 20, paddingBottom: 40, gap: 16},
    topRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    typeBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'flex-start',
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    typeBadgeText: {fontSize: 11, fontWeight: '700'},
    previewBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    previewBtnText: {fontSize: 11, fontWeight: '700'},
    question: {
      color: chrome.text,
      fontSize: 20,
      fontWeight: '700',
      lineHeight: 28,
      paddingVertical: 0,
    },
    sectionLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    wordRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    wordCard: {
      flex: 1,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      backgroundColor: chrome.fill,
      paddingHorizontal: 10,
      paddingVertical: 7,
    },
    wordInput: {
      color: chrome.text,
      fontSize: 13,
      fontWeight: '700',
      paddingVertical: 0,
    },
    addBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingVertical: 10,
      paddingHorizontal: 14,
      borderRadius: 12,
      borderWidth: 1.5,
      borderStyle: 'dashed',
      borderColor: chrome.borderStrong,
    },
    addTxt: {color: chrome.textFaint, fontSize: 13},
    generateBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingVertical: 12,
      borderRadius: 12,
      borderWidth: 1.5,
      marginTop: 4,
    },
    generateTxt: {fontSize: 13, fontWeight: '700'},
    gridStatus: {alignItems: 'center', marginTop: 4},
    gridStatusText: {color: chrome.success, fontSize: 12},
    explanationWrap: {gap: 6},
    explanationLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    explanationCard: {
      backgroundColor: chrome.fill,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    explanationInput: {
      color: chrome.textMuted,
      fontSize: 13,
      lineHeight: 18,
      paddingVertical: 0,
    },
  })

/** The editor's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
